import * as React from 'react';
import { BidHistoryItem } from '@/types/BidHistoryItem';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidHistory } from '@/redux/modules/bidHistory';
import { getItemModelCurrency, getItemModelStartPrice } from '@/redux/modules/item/summary/itemSummary.selectors';
import { useAppSelector } from '@/redux/hooks';
import styled from 'styled-components';

type Props = {
    itemId: number;
    showBidHistory: boolean;
};

const BidHistoryTable = ({ itemId, showBidHistory }: Props) => {
    const bidHistory = useAppSelector((state) => getBidHistory(state, itemId));
    const startPrice = useAppSelector((state) => getItemModelStartPrice(state, itemId)) || 1;
    const currency = useAppSelector((state) => getItemModelCurrency(state, itemId));

    const [shownBidHistory, setShownBidHistory] = React.useState(
        showBidHistory ? bidHistory : Boolean(bidHistory.length) && bidHistory.slice(0, 3)
    );

    React.useEffect(() => {
        setShownBidHistory(showBidHistory ? bidHistory : bidHistory && bidHistory.slice(0, 3));
    }, [bidHistory, showBidHistory]);

    const hideStartPrice = !showBidHistory && shownBidHistory.length > 2;

    const bidHistoryMapping = React.useCallback(
        (bid: BidHistoryItem, index: number) => {
            return (
                <TableRowDiv key={index}>
                    <TableCellDiv>{bid.source}</TableCellDiv>
                    <TableCellDiv>
                        <FormattedNumber
                            currency={currency}
                            key="bid-history-table-bid"
                            style="currency"
                            value={bid.amount}
                        />
                    </TableCellDiv>
                </TableRowDiv>
            );
        },
        [currency]
    );

    if (shownBidHistory?.length > 0) {
        return (
            <div id="bid-history">
                <TableDiv>
                    <BidHistoryDiv>
                        <HeaderRowDiv>
                            <TableCellDiv>
                                <FormattedMessage id="bidder" />
                            </TableCellDiv>
                            <TableCellDiv>
                                <FormattedMessage id="bid" />
                            </TableCellDiv>
                        </HeaderRowDiv>
                        {shownBidHistory?.map(bidHistoryMapping)}
                        {!hideStartPrice && (
                            <RowDiv>
                                <TableCellDiv>
                                    <FormattedMessage id="starting_bid" />
                                </TableCellDiv>
                                <TableCellDiv>
                                    <FormattedNumber
                                        currency={currency}
                                        key="bid-history-table-starting-bid"
                                        style="currency"
                                        value={startPrice}
                                    />
                                </TableCellDiv>
                            </RowDiv>
                        )}
                    </BidHistoryDiv>
                </TableDiv>
            </div>
        );
    }
    return null;
};

export default React.memo<Props>(BidHistoryTable);

const TableRowDiv = styled.div`
    display: table-row;
    border-top: ${({ theme }) => `1px solid ${theme.colors.grey400}`};

    @media print {
        border: none;
    }
`;

const TableCellDiv = styled.div`
    display: table-cell;
    text-transform: uppercase;
    border-right: ${({ theme }) => `1px solid ${theme.colors.grey400}`};
    padding: 13px 15px;

    @media print {
        padding: 0;
        border: none;
    }

    &:last-child {
        border-right: none;
    }
`;

const TableDiv = styled.div`
    flex: 0 0 auto;

    @media print {
        margin-top: 40px;
        max-width: 350px;
        position: absolute;
        top: 600px;
        right: 400px;
        width: 200px;
    }
`;

const BidHistoryDiv = styled.div`
    max-width: 400px;
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    font-size: 0.8em;
    color: ${({ theme }) => theme.colors.black};
    display: table;
    table-layout: fixed;
    width: 100%;
`;

const HeaderRowDiv = styled.div`
    display: table-row;
    background-color: ${({ theme }) => theme.colors.grey400};
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.grey100};
    border-bottom: ${({ theme }) => `1px solid ${theme.colors.grey400}`};

    @media print {
        border: none;
    }
`;

const RowDiv = styled.div`
    display: table-row;
    border-top: ${({ theme }) => `1px solid ${theme.colors.grey400}`};
    color: ${({ theme }) => theme.colors.grey100};

    @media print {
        border: none;
    }
`;
